import React from "react";
import {
  Row,
  Col,
  FormGroup,
  ControlLabel,
  FormControl,
} from "react-bootstrap";
import CreatableSelect from "react-select/lib/Creatable";
import Button from "components/CustomButton/CustomButton.jsx";
import {
  DISTRICTS,
  PARTNER_FEATURES,
  partnerNameOptions,
} from "./partnerFormUtils";
import "./partners.css";

const formatFeatureLabel = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const PartnerForm = (props) => {
  const partnerOptions = partnerNameOptions([
    props.value.name,
    ...(props.partnerNames || []),
  ]);
  const selectedPartnerOption = props.value.name
    ? partnerOptions.find((option) => option.value === props.value.name) || {
        value: props.value.name,
        label: props.value.name,
      }
    : null;
  const selectedFeatures = props.value.partnerFeatures || [];
  const selectedFeature = (type) =>
    selectedFeatures.find((feature) => feature.type === type);
  const featureDefinitions = [
    ...PARTNER_FEATURES.map((definition) => {
      const selection = selectedFeature(definition.type);
      const definedSubtypes = definition.subtypes.map(
        (subtype) => subtype.value,
      );
      const additionalSubtypes = ((selection && selection.subtypes) || [])
        .filter((subtype) => !definedSubtypes.includes(subtype))
        .map((subtype) => ({
          value: subtype,
          label: formatFeatureLabel(subtype),
        }));

      return {
        ...definition,
        subtypes: [...definition.subtypes, ...additionalSubtypes],
      };
    }),
    ...selectedFeatures
      .filter(
        (selection) =>
          !PARTNER_FEATURES.some(
            (definition) => definition.type === selection.type,
          ),
      )
      .map((selection) => ({
        type: selection.type,
        label: formatFeatureLabel(selection.type),
        subtypes: (selection.subtypes || []).map((subtype) => ({
          value: subtype,
          label: formatFeatureLabel(subtype),
        })),
      })),
  ];

  return (
    <div className="partner-form">
      <Row>
        <Col md={12}>
          <FormGroup>
            <ControlLabel id="partner-status-label">Status</ControlLabel>
            <div>
              <button
                type="button"
                role="switch"
                aria-checked={!props.value.isInactive}
                aria-labelledby="partner-status-label"
                className={
                  "partner-status-toggle " +
                  (props.value.isInactive ? "is-inactive" : "is-active")
                }
                onClick={() =>
                  props.onChange({
                    target: {
                      name: "isInactive",
                      type: "checkbox",
                      checked: !props.value.isInactive,
                    },
                  })
                }
                disabled={props.disabled}
              >
                <span
                  className="partner-status-toggle-track"
                  aria-hidden="true"
                >
                  <span className="partner-status-toggle-knob" />
                </span>
                <span className="partner-status-toggle-label">
                  {props.value.isInactive ? "Inactive" : "Active"}
                </span>
              </button>
            </div>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup validationState={props.errors.name ? "error" : null}>
            <ControlLabel>Service Partner *</ControlLabel>
            <CreatableSelect
              className="react-select partner-search-select"
              classNamePrefix="react-select"
              name="name"
              value={selectedPartnerOption}
              options={partnerOptions}
              placeholder="Select or enter partner"
              formatCreateLabel={(inputValue) =>
                `Use "${inputValue}" as a new Partner`
              }
              noOptionsMessage={() =>
                props.partnerNamesLoading
                  ? "Loading partner names..."
                  : "No partner found. Type a name and press Enter to use it."
              }
              isSearchable
              isClearable
              isLoading={props.partnerNamesLoading}
              isDisabled={props.disabled}
              onChange={(option) =>
                props.onChange({
                  target: {
                    name: "name",
                    value: option ? option.value : "",
                  },
                })
              }
              hideSelectedOptions={false}
            />
            {props.errors.name && (
              <span className="help-block">{props.errors.name}</span>
            )}
          </FormGroup>
        </Col>
        <Col md={4}>
          <FormGroup validationState={props.errors.branchName ? "error" : null}>
            <ControlLabel>Branch Location *</ControlLabel>
            <FormControl
              type="text"
              name="branchName"
              value={props.value.branchName || ""}
              placeholder="Enter branch name"
              onChange={props.onChange}
              disabled={props.disabled}
            />
            {props.errors.branchName && (
              <span className="help-block">{props.errors.branchName}</span>
            )}
          </FormGroup>
        </Col>
        <Col md={2}>
          <FormGroup>
            <ControlLabel>Quote availability</ControlLabel>
            <label className="partner-quote-option">
              <input
                type="checkbox"
                name="quoteAvailable"
                checked={Boolean(props.value.quoteAvailable)}
                onChange={props.onChange}
                disabled={props.disabled}
              />
              <span>Quote available</span>
            </label>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <FormGroup>
            <div className="partner-feature-heading">
              <div>
                <ControlLabel>Services Offered</ControlLabel>
                <span className="partner-optional-badge">Optional</span>
              </div>
            </div>
            <div className="partner-feature-grid">
              {featureDefinitions.map((feature) => {
                const selection = selectedFeature(feature.type);
                const enabled = Boolean(selection);

                return (
                  <div
                    className={
                      "partner-feature-card" + (enabled ? " is-selected" : "")
                    }
                    key={feature.type}
                  >
                    <label className="partner-feature-type">
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(event) =>
                          props.onFeatureTypeChange(
                            feature.type,
                            event.target.checked,
                          )
                        }
                        disabled={props.disabled}
                      />
                      <span>{feature.label}</span>
                    </label>
                    <div className="partner-feature-divider" />
                    <div className="partner-feature-subtype-options">
                      {feature.subtypes.length === 0 ? (
                        <span className="partner-feature-empty">
                          No subtypes supplied
                        </span>
                      ) : (
                        feature.subtypes.map((subtype) => (
                          <label
                            className="partner-feature-checkbox"
                            key={subtype.value}
                          >
                            <input
                              type="checkbox"
                              checked={
                                enabled &&
                                (selection.subtypes || []).includes(
                                  subtype.value,
                                )
                              }
                              onChange={(event) =>
                                props.onFeatureSubtypeChange(
                                  feature.type,
                                  subtype.value,
                                  event.target.checked,
                                )
                              }
                              disabled={!enabled || props.disabled}
                            />
                            <span>{subtype.label}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <span className="partner-field-hint">
              Choose each service this branch provides. Subtypes are optional.
            </span>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup validationState={props.errors.district ? "error" : null}>
            <ControlLabel>District *</ControlLabel>
            <FormControl
              componentClass="select"
              name="district"
              value={props.value.district || ""}
              onChange={props.onChange}
              disabled={props.disabled}
            >
              {DISTRICTS.map((district) => (
                <option key={district.id} value={district.id}>
                  {district.name}
                </option>
              ))}
            </FormControl>
            {props.errors.district && (
              <span className="help-block">{props.errors.district}</span>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup
            validationState={props.errors.coordinates ? "error" : null}
          >
            <ControlLabel>Branch Location Coordinates *</ControlLabel>
            <div className="partner-coordinate-input">
              <FormControl
                type="text"
                name="coordinates"
                value={props.value.coordinates || ""}
                placeholder="Latitude, Longitude"
                onChange={props.onChange}
                disabled={props.disabled}
              />
              <Button
                bsStyle="info"
                type="button"
                className="partner-location-button"
                onClick={props.onGetLocation}
                disabled={props.disabled}
              >
                <i className="fa fa-map-marker" />
                Get Location
              </Button>
            </div>
            <span className="partner-field-hint">
              Open Google Maps, copy the coordinates, and paste them here.
            </span>
            {props.errors.coordinates && (
              <span className="help-block">{props.errors.coordinates}</span>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup validationState={props.errors.mobile ? "error" : null}>
            <ControlLabel>Branch Contact Number *</ControlLabel>
            <div className="partner-mobile-input">
              <span className="partner-mobile-prefix">+94</span>
              <FormControl
                type="tel"
                name="mobile"
                maxLength={9}
                value={props.value.mobile || ""}
                placeholder="77 123 4567"
                onChange={props.onChange}
                disabled={props.disabled}
              />
            </div>
            {props.errors.mobile && (
              <span className="help-block">{props.errors.mobile}</span>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup validationState={props.errors.logo ? "error" : null}>
            <ControlLabel>Partner Logo *</ControlLabel>
            <FormControl
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={props.onLogoChange}
              disabled={props.disabled}
            />
            <span className="partner-field-hint">
              JPG, PNG or WebP, maximum 2 MB.
            </span>
            {props.value.logo && (
              <div className="partner-logo-preview-wrap">
                <img
                  className="partner-logo-preview"
                  src={props.value.logo}
                  alt="Selected partner logo"
                />
              </div>
            )}
            {props.errors.logo && (
              <span className="help-block">{props.errors.logo}</span>
            )}
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
};

export default PartnerForm;
